import { v4 as uuidv4 } from 'uuid';
import { getCurrentConference } from '../base/conference/functions';
import { JitsiConferenceEvents } from '../base/lib-jitsi-meet';
import { getLocalParticipant, getParticipantDisplayName } from '../base/participants/functions';
import MiddlewareRegistry from '../base/redux/MiddlewareRegistry';
import StateListenerRegistry from '../base/redux/StateListenerRegistry';
import { addMessage, editMessage } from '../chat/actions.any';
import { ChatTabs, MESSAGE_TYPE_LOCAL, MESSAGE_TYPE_REMOTE } from '../chat/constants';
import { showErrorNotification, showNotification, showSuccessNotification } from '../notifications/actions';
import { NOTIFICATION_TIMEOUT_TYPE, NOTIFICATION_TYPE } from '../notifications/constants';
import { I_AM_VISITOR_MODE } from '../visitors/actionTypes';
import { ADD_FILE, DOWNLOAD_FILE, REMOVE_FILE, UPDATE_FILE_UPLOAD_PROGRESS, UPLOAD_FILES, _FILE_LIST_RECEIVED, _FILE_REMOVED } from './actionTypes';
import { addFile, removeFile, updateFileProgress } from './actions';
import { getFileExtension, getHttpUploadComponentAddress } from './functions.any';
import { uploadFileViaHttp } from './httpFileUpload';
import logger from './logger';
import { downloadFile } from './utils';
/**
 * Registers a change handler for state['features/base/conference'].conference to
 * set the event listeners needed for the file sharing feature to operate.
 */
StateListenerRegistry.register(state => state['features/base/conference'].conference, (conference, { dispatch, getState }, previousConference) => {
    if (conference && !previousConference) {
        conference.on(JitsiConferenceEvents.FILE_SHARING_FILE_ADDED, (file) => {
            const state = getState();
            const localParticipant = getLocalParticipant(state);
            const isRemoteFile = file.authorParticipantId !== localParticipant?.id;
            const { isOpen, focusedTab } = state['features/chat'];
            const isFileSharingTabVisible = isOpen && focusedTab === ChatTabs.FILE_SHARING;
            dispatch(addFile(file, isRemoteFile && !isFileSharingTabVisible));
            if (isRemoteFile && !isFileSharingTabVisible) {
                dispatch(showNotification({
                    titleKey: 'fileSharing.newFileNotification',
                    titleArguments: { participantName: file.authorParticipantName, fileName: file.fileName }
                }, NOTIFICATION_TIMEOUT_TYPE.SHORT));
            }
        });
        conference.on(JitsiConferenceEvents.FILE_SHARING_FILE_REMOVED, (fileId) => {
            const state = getState();
            const localParticipant = getLocalParticipant(state);
            const { files } = state['features/file-sharing'];
            const { isOpen, focusedTab } = state['features/chat'];
            const removedFile = files.get(fileId);
            const isFileSharingTabVisible = isOpen && focusedTab === ChatTabs.FILE_SHARING;
            if (removedFile && removedFile.authorParticipantId === localParticipant?.id && !isFileSharingTabVisible) {
                dispatch(showNotification({
                    titleKey: 'fileSharing.fileRemovedByOther',
                    titleArguments: { fileName: removedFile.fileName },
                    appearance: NOTIFICATION_TYPE.WARNING
                }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
            }
            dispatch({
                type: _FILE_REMOVED,
                fileId
            });
            // Notify external API about file deletion (for remote participants)
            APP.API.notifyFileDeleted(fileId);
        });
        conference.on(JitsiConferenceEvents.FILE_SHARING_FILES_RECEIVED, (files) => {
            const state = getState();
            const localParticipant = getLocalParticipant(state);
            dispatch({
                type: _FILE_LIST_RECEIVED,
                files,
                localParticipantId: localParticipant?.id
            });
            // Notify external API about existing files (for participants joining late)
            Object.values(files).forEach((file) => {
                APP.API.notifyFileUploaded(file);
            });
        });
    }
});
/**
 * Middleware that handles file sharing actions.
 *
 * @param {Store} store - The redux store.
 * @returns {Function}
 */
MiddlewareRegistry.register(store => next => action => {
    switch (action.type) {
        case I_AM_VISITOR_MODE: {
            if (!action.iAmVisitor) {
                const state = store.getState();
                const conference = getCurrentConference(state);
                conference?.getFileSharing()?.requestFileList?.();
            }
            return next(action);
        }
        case UPLOAD_FILES: {
            for (const file of action.files) {
                uploadFile(file, store);
            }
            return next(action);
        }
        case UPDATE_FILE_UPLOAD_PROGRESS:
        case ADD_FILE: {
            const result = next(action);
            const state = store.getState();
            const { files } = state['features/file-sharing'];
            const file = action.type === ADD_FILE ? action.file : files.get(action.fileId);
            if (!file) {
                return result;
            }
            const localParticipant = getLocalParticipant(state);
            const isLocalFile = localParticipant?.id === file.authorParticipantId;
            // Only dispatch chat message for fully uploaded files (progress === 100).
            // Files that are still uploading have progress < 100, so we skip creating the message.
            // Once upload completes, for the local participant the file is broadcast with progress: 100 and the message
            // is created. Remote participants receive the file metadata only once the file is successfully uploaded and
            // the progress field will be undefined.
            if (file.progress === 100 || !isLocalFile) {
                store.dispatch(addMessage({
                    displayName: file.authorParticipantName,
                    // The metadata only carries a small download URL (no file bytes), so it is
                    // safe to keep it in the chat state.
                    fileMetadata: file,
                    hasRead: isLocalFile,
                    isReaction: false,
                    lobbyChat: false,
                    message: '', // Empty message as the file metadata contains all info
                    messageId: file.fileId,
                    messageType: isLocalFile ? MESSAGE_TYPE_LOCAL : MESSAGE_TYPE_REMOTE,
                    participantId: file.authorParticipantId,
                    privateMessage: false,
                    timestamp: file.timestamp
                }));
                APP.API.notifyFileUploaded(file);
            }
            return result;
        }
        case REMOVE_FILE: {
            const state = store.getState();
            const conference = getCurrentConference(state);
            const { files } = state['features/file-sharing'];
            const fileId = action.fileId;
            const existingMetadata = files.get(fileId);
            // ignore remove a file till the file is actually uploaded
            if (!conference || (existingMetadata?.progress ?? 100) !== 100) {
                return next(action);
            }
            // Remove the file from the file-sharing component. The Prosody component drops it
            // from the room's in-memory list and notifies the other occupants; there is no
            // external storage to clean up.
            conference.getFileSharing().removeFile(fileId);
            // remove it from local state
            store.dispatch({
                type: _FILE_REMOVED,
                fileId
            });
            // Notify external API about file deletion (for the local participant who deleted it)
            APP.API.notifyFileDeleted(fileId);
            store.dispatch(showSuccessNotification({
                titleKey: 'fileSharing.removeFileSuccess'
            }, NOTIFICATION_TIMEOUT_TYPE.SHORT));
            return next(action);
        }
        case DOWNLOAD_FILE: {
            const state = store.getState();
            const { files } = state['features/file-sharing'];
            const file = files.get(action.fileId);
            if (!file?.fileUrl) {
                store.dispatch(showErrorNotification({
                    titleKey: 'fileSharing.downloadFailedTitle',
                    descriptionKey: 'fileSharing.downloadFailedDescription',
                    appearance: NOTIFICATION_TYPE.ERROR
                }, NOTIFICATION_TIMEOUT_TYPE.STICKY));
                return next(action);
            }
            store.dispatch(showNotification({
                titleKey: 'fileSharing.downloadStarted'
            }, NOTIFICATION_TIMEOUT_TYPE.SHORT));
            // The file lives on the ephemeral storage server; fetch() downloads it by URL.
            downloadFile(file.fileUrl, file.fileName).catch((error) => {
                logger.warn('Could not download file:', error);
                store.dispatch(showErrorNotification({
                    titleKey: 'fileSharing.downloadFailedTitle',
                    descriptionKey: 'fileSharing.downloadFailedDescription',
                    appearance: NOTIFICATION_TYPE.ERROR
                }, NOTIFICATION_TIMEOUT_TYPE.STICKY));
            });
            return next(action);
        }
        case _FILE_REMOVED: {
            const result = next(action);
            const state = store.getState();
            const { messages } = state['features/chat'];
            // Find the message corresponding to this file and mark it as deleted.
            const fileMessage = messages.find(msg => msg.messageId === action.fileId);
            if (fileMessage?.fileMetadata) {
                // Replace the file metadata with just the isDeleted flag to avoid keeping unnecessary data.
                store.dispatch(editMessage({
                    ...fileMessage,
                    fileMetadata: {
                        isDeleted: true
                    }
                }));
            }
            return result;
        }
    }
    return next(action);
});
/**
 * Uploads a file out-of-band via XEP-0363 (HTTP File Upload) and shares its download URL
 * in-band over XMPP.
 *
 * Custom (Vianova): there is no permanent storage service. The file bytes are PUT to an
 * ephemeral storage component (Prosody mod_http_file_share) and only the resulting GET URL
 * is carried inside the file-sharing metadata, stored in Prosody room memory and broadcast
 * to the other occupants (and to late joiners). The stored file disappears when it expires
 * or when the room is destroyed.
 *
 * @param {File} file - The file to share.
 * @param {IStore} store - The redux store.
 * @returns {void}
 */
function uploadFile(file, store) {
    const state = store.getState();
    const conference = getCurrentConference(state);
    const localParticipant = getLocalParticipant(state);
    const { connection } = state['features/base/connection'];
    const roomJid = conference?.room?.roomjid;
    const jid = connection.getJid();
    const fileId = uuidv4();
    const fileMetadata = {
        authorParticipantId: localParticipant.id,
        authorParticipantJid: jid,
        authorParticipantName: getParticipantDisplayName(state, localParticipant.id),
        conferenceFullName: roomJid ?? '',
        fileId,
        fileName: file.name,
        fileSize: file.size,
        fileType: getFileExtension(file.name),
        timestamp: Date.now()
    };
    store.dispatch(addFile(fileMetadata));
    store.dispatch(updateFileProgress(fileId, 1));
    const handleError = () => {
        logger.warn('Could not upload file for sharing:', file.name);
        store.dispatch(removeFile(fileId));
        store.dispatch(showErrorNotification({
            titleKey: 'fileSharing.uploadFailedTitle',
            descriptionKey: 'fileSharing.uploadFailedDescription',
            appearance: NOTIFICATION_TYPE.ERROR
        }, NOTIFICATION_TIMEOUT_TYPE.STICKY));
    };
    const fileSharingHandler = conference?.getFileSharing();
    if (!fileSharingHandler?.isSupported?.()) {
        logger.warn('File sharing component is not available on the backend.');
        handleError();
        return;
    }
    const uploadComponent = getHttpUploadComponentAddress(state);
    const xmppConnection = connection?.xmpp?.connection;
    if (!uploadComponent || !xmppConnection?.sendIQ) {
        logger.warn('HTTP file upload is not configured (missing upload component or connection).');
        handleError();
        return;
    }
    uploadFileViaHttp(xmppConnection, uploadComponent, file, percent => {
        store.dispatch(updateFileProgress(fileId, percent));
    })
        .then(fileUrl => {
        // Store the URL locally (with progress 100) so the sender can also download it;
        // this also creates the corresponding chat message.
        store.dispatch(addFile({
            ...fileMetadata,
            fileUrl,
            progress: 100
        }));
        // Broadcast the file metadata (with the download URL) to the other occupants via
        // the Prosody component, which also serves it to late joiners.
        fileSharingHandler.addFile({
            ...fileMetadata,
            fileUrl
        });
        store.dispatch(showSuccessNotification({
            titleKey: 'fileSharing.fileUploadedSuccessfully'
        }, NOTIFICATION_TIMEOUT_TYPE.SHORT));
    })
        .catch(error => {
        logger.warn('Could not upload file for sharing:', error);
        handleError();
    });
}
